export type JsonCallbackEvent = {
  eventType: "JsonCallback";
  callbackId: string;
  payload: Record<string, unknown>;
};
export type ViewWillAppearEvent = {
  eventType: "ViewWillAppear";
  viewName: string;
  viewInstanceId: string;
  rootTag?: number;
  data: Record<string, unknown>;
};
export type ViewWillDisappearEvent = {
  eventType: "ViewWillDisappear";
  viewName: string;
  viewInstanceId: string;
  rootTag?: number;
  data: Record<string, unknown>;
};
export type ViewDidDisappearEvent = {
  eventType: "ViewDidDisappear";
  viewName: string;
  viewInstanceId: string;
  data: Record<string, unknown>;
};
export type FormCompletedEvent = {
  eventType: "FormCompleted";
  formName: string;
  formInstanceId?: string;
  result: "completed" | "cancelled" | "skipped";
};
export type ScreenshotEvent = { eventType: "Screenshot"; screen: string };
export type UserDidLoginEvent = { eventType: "UserDidLogin" };
export type UserDidLogoutEvent = { eventType: "UserDidLogout" };
/** Dispatched after Proctor results are updated in memory and allocations are ready to read. */
export type ProctorUpdatedEvent = { eventType: "ProctorUpdated" };
export type DataUpdatedEvent = { eventType: "dataUpdated"; name: string };
export type ApplyType = "autoApply";
export type ApplyStartEvent = { eventType: "ApplyStart"; applyType: ApplyType };
export type ApplyFinishedEvent = {
  eventType: "ApplyFinished";
  status: "success" | "cancel";
  jobKey?: string;
};
export type RefreshAceChatEvent = {
  eventType: "RefreshAceChat";
  conversationId?: string | null;
  starterMessage?: string | null;
  agentType?: string | null;
  termType?: string | null;
  url?: string | null;
  sourceContext?: string | null;
};
export type PopToAceRootViewEvent = { eventType: "PopToAceRootView" };
export type AceBottomNavTabBlurredEvent = { eventType: "AceBottomNavTabBlurred" };
export type TabBackPressRequestedEvent = {
  eventType: "AndroidInternal.TabBackPressRequested";
  componentName: string;
  requestId: string;
};
export type TabBackPressResultEvent = {
  eventType: "AndroidInternal.TabBackPressResult";
  requestId: string;
  handled: boolean;
};

export type BroadcastEvent =
  | JsonCallbackEvent
  | ViewWillAppearEvent
  | ViewWillDisappearEvent
  | ViewDidDisappearEvent
  | FormCompletedEvent
  | ScreenshotEvent
  | UserDidLoginEvent
  | UserDidLogoutEvent
  | ProctorUpdatedEvent
  | DataUpdatedEvent
  | ApplyStartEvent
  | ApplyFinishedEvent
  | RefreshAceChatEvent
  | PopToAceRootViewEvent
  | AceBottomNavTabBlurredEvent
  | TabBackPressRequestedEvent
  | TabBackPressResultEvent;
export type BroadcastEventMap = {
  ViewWillAppear: ViewWillAppearEvent;
  ViewWillDisappear: ViewWillDisappearEvent;
  ViewDidDisappear: ViewDidDisappearEvent;
  JsonCallback: JsonCallbackEvent;
  FormCompleted: FormCompletedEvent;
  Screenshot: ScreenshotEvent;
  UserDidLogin: UserDidLoginEvent;
  UserDidLogout: UserDidLogoutEvent;
  ProctorUpdated: ProctorUpdatedEvent;
  DataUpdated: DataUpdatedEvent;
  ApplyStart: ApplyStartEvent;
  ApplyFinished: ApplyFinishedEvent;
  RefreshAceChat: RefreshAceChatEvent;
  PopToAceRootView: PopToAceRootViewEvent;
  AceBottomNavTabBlurred: AceBottomNavTabBlurredEvent;
  TabBackPressRequested: TabBackPressRequestedEvent;
  TabBackPressResult: TabBackPressResultEvent;
};

/** Native subscription channels; these can differ from the payload's eventType. */
export const BroadcastEventName = {
  ViewWillAppear: "ViewWillAppear",
  ViewWillDisappear: "ViewWillDisappear",
  ViewDidDisappear: "ViewDidDisappear",
  JsonCallback: "JsonCallback",
  FormCompleted: "FormCompleted",
  Screenshot: "Screenshot",
  UserDidLogin: "UserDidLogin",
  UserDidLogout: "UserDidLogout",
  ProctorUpdated: "ProctorUpdated",
  DataUpdated: "DataUpdated",
  ApplyStart: "ApplyStart",
  ApplyFinished: "ApplyFinished",
  RefreshAceChat: "RefreshAceChat",
  PopToAceRootView: "PopToAceRootView",
  AceBottomNavTabBlurred: "AceBottomNavTabBlurred",
  TabBackPressRequested: "TabBackPressRequested",
  TabBackPressResult: "TabBackPressResult",
} as const satisfies { [Name in keyof BroadcastEventMap]: Name };

export type BroadcastEventName = keyof BroadcastEventMap;

/** Construct native payloads without repeating their wire discriminants at call sites. */
export const BroadcastEvent = {
  jsonCallback(data: Omit<JsonCallbackEvent, "eventType">): JsonCallbackEvent {
    return { ...data, eventType: "JsonCallback" };
  },
  viewWillAppear(data: Omit<ViewWillAppearEvent, "eventType">): ViewWillAppearEvent {
    return { ...data, eventType: "ViewWillAppear" };
  },
  viewWillDisappear(data: Omit<ViewWillDisappearEvent, "eventType">): ViewWillDisappearEvent {
    return { ...data, eventType: "ViewWillDisappear" };
  },
  viewDidDisappear(data: Omit<ViewDidDisappearEvent, "eventType">): ViewDidDisappearEvent {
    return { ...data, eventType: "ViewDidDisappear" };
  },
  formCompleted(data: Omit<FormCompletedEvent, "eventType">): FormCompletedEvent {
    return { ...data, eventType: "FormCompleted" };
  },
  screenshot(data: Omit<ScreenshotEvent, "eventType">): ScreenshotEvent {
    return { ...data, eventType: "Screenshot" };
  },
  userDidLogin(): UserDidLoginEvent {
    return { eventType: "UserDidLogin" };
  },
  userDidLogout(): UserDidLogoutEvent {
    return { eventType: "UserDidLogout" };
  },
  proctorUpdated(): ProctorUpdatedEvent {
    return { eventType: "ProctorUpdated" };
  },
  dataUpdated(data: Omit<DataUpdatedEvent, "eventType">): DataUpdatedEvent {
    return { ...data, eventType: "dataUpdated" };
  },
  applyStart(data: Omit<ApplyStartEvent, "eventType">): ApplyStartEvent {
    return { ...data, eventType: "ApplyStart" };
  },
  applyFinished(data: Omit<ApplyFinishedEvent, "eventType">): ApplyFinishedEvent {
    return { ...data, eventType: "ApplyFinished" };
  },
  refreshAceChat(data: Omit<RefreshAceChatEvent, "eventType">): RefreshAceChatEvent {
    return { ...data, eventType: "RefreshAceChat" };
  },
  popToAceRootView(): PopToAceRootViewEvent {
    return { eventType: "PopToAceRootView" };
  },
  aceBottomNavTabBlurred(): AceBottomNavTabBlurredEvent {
    return { eventType: "AceBottomNavTabBlurred" };
  },
  tabBackPressRequested(
    data: Omit<TabBackPressRequestedEvent, "eventType">,
  ): TabBackPressRequestedEvent {
    return { ...data, eventType: "AndroidInternal.TabBackPressRequested" };
  },
  tabBackPressResult(data: Omit<TabBackPressResultEvent, "eventType">): TabBackPressResultEvent {
    return { ...data, eventType: "AndroidInternal.TabBackPressResult" };
  },
};

export interface JSTBroadcastSubscription {
  remove: () => Promise<void>;
}
